import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import moment from 'moment';
import Input from '@material-ui/core/Input';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import AssessmentIcon from '@material-ui/icons/Assessment';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import PeopleIcon from '@material-ui/icons/People';
import DescriptionIcon from '@material-ui/icons/Description';
import ScheduleIcon from '@material-ui/icons/Schedule';
import Paper from '~/components/Paper';
import Chip from '~/components/Chip';
import InputShow from '~/components/InputShow';
import InputControl from '~/components/Input/InputControl';
import InputNumber from '~/components/Input/InputNumber';
import InputSelect from '~/components/Input/InputSelect';
import InputDate from '~/components/Input/InputDate';
import InputSwitch from '~/components/Input/InputSwitch';
import Form from '~/containers/Form';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Request from '~/providers/Request';
import WhatsappCampaignSettingsRecord from '~/records/WhatsappCampaignSettingsRecord';
import { historyPropType } from '~/config/routerPropTypes';
import { whatsappCampaignInvitations } from '~/config/routes';
import { URL } from '~/config/uri';
import useStyles from './styles';

const PATH = '/admin/whatsapp_campaign';
const CLOCK_DEAD_MINUTES = 30;
const SUMMARY_KEYS = ['trigger', 'scanned', 'sent', 'failed', 'skipped', 'stopped_reason', 'error'];
const FUNNEL_KEYS = ['never_accessed', 'old_enough', 'valid_mobile', 'valid_cpf_length', 'has_validation_data', 'already_invited', 'skipped', 'eligible_now'];
const RESULT_ROWS = [
  { key: 'invited', tone: 'neutral' },
  { key: 'delivered', tone: 'good' },
  { key: 'undeliverable', tone: 'bad' },
  { key: 'consented', tone: 'good' },
  { key: 'declined', tone: 'bad' },
  { key: 'awaiting_birth_month', tone: 'neutral' },
  { key: 'awaiting_surname', tone: 'neutral' },
  { key: 'awaiting_cpf_digits', tone: 'neutral' },
  { key: 'validation_failed', tone: 'bad' },
  { key: 'already_active', tone: 'neutral' },
  { key: 'validated', tone: 'good', highlight: true },
  { key: 'entered_app', tone: 'good', highlight: true },
];
const TONE_CLASS = { good: 'toneGood', bad: 'toneBad', neutral: 'toneNeutral' };
const ALL_TEMPLATES = 'all';

const numberInputProps = { styleInput: { marginTop: 30 }, precision: 0, showMask: false };

const resultsParams = (template, sentFrom, sentUntil) => {
  const params = template && template !== ALL_TEMPLATES ? { template_name: template } : {};
  if (sentFrom) params.sent_from = moment(sentFrom).startOf('day').format();
  if (sentUntil) params.sent_until = moment(sentUntil).endOf('day').format();
  return params;
};

async function downloadResultsPdf(params, tr, setDownloading) {
  setDownloading(true);
  const toastId = toast.info(tr('pdfGenerating'), { autoClose: false });
  try {
    const res = await fetch(`${URL}${PATH}/results/pdf?${new URLSearchParams(params)}`, { headers: await Request.getHeaders() });
    if (!res.ok) throw new Error(res.statusText);

    const href = window.URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = href;
    link.download = `resultados-campanha-whatsapp-${moment().format('YYYY-MM-DD-HHmm')}.pdf`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
    toast.success(tr('pdfDone'));
  } catch (e) {
    toast.error(tr('pdfError'));
  } finally {
    toast.dismiss(toastId);
    setDownloading(false);
  }
}

const emptyResultsKey = (template, sentFrom, sentUntil) => {
  if (sentFrom || sentUntil) return 'resultsEmptyPeriod';
  return template === ALL_TEMPLATES ? 'resultsEmpty' : 'resultsEmptyTemplate';
};

function Settings({ history }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key, options) => t(`whatsappCampaign.extras.${key}`, options);
  const attrName = (attr) => WhatsappCampaignSettingsRecord.hummanAttributeName(attr);

  const [record, setRecord] = useState(new WhatsappCampaignSettingsRecord());
  const [loading, setLoading] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [template, setTemplate] = useState(ALL_TEMPLATES);
  const [sentFrom, setSentFrom] = useState('');
  const [sentUntil, setSentUntil] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Request.get(`${PATH}/settings`, {});
      setRecord((current) => current.updateAttributes(response.data));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFunnel = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const response = await Request.get(`${PATH}/preview`, {}, { normalizeJSON: false });
      setFunnel(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setFunnelLoading(false);
    }
  }, []);

  const loadResults = useCallback(async (templateName, from, until) => {
    setResultsLoading(true);
    try {
      const response = await Request.get(`${PATH}/results`, resultsParams(templateName, from, until), { normalizeJSON: false });
      setResults(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadFunnel();
  }, [load, loadFunnel]);

  useEffect(() => {
    loadResults(template, sentFrom, sentUntil);
  }, [loadResults, template, sentFrom, sentUntil]);

  const handleChange = (attrs) => setRecord(record.updateAttributes(attrs));

  async function onSubmit() {
    try {
      const response = await Request.put(`${PATH}/settings`, record.toParams(), {}, { controller: 'whatsappCampaign' });
      setRecord(record.updateAttributes(response.data));
      toast.success(tr('saved'));
    } catch (e) {
      console.error(e);
    }
  }

  async function runNow() {
    try {
      await Request.post(`${PATH}/run`, {});
      toast.success(tr('runRequested'));
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setConfirmRun(false);
    }
  }

  const hasToken = Boolean(record.get('has_api_token'));
  const envToken = Boolean(record.get('env_api_token'));
  const lastTick = record.get('last_tick_at');
  const tickMinutes = lastTick ? moment().diff(moment(lastTick), 'minutes') : null;
  const clockAlive = Boolean(lastTick) && tickMinutes <= CLOCK_DEAD_MINUTES;
  const lastRun = record.get('last_run_at');
  const summary = record.get('last_run_summary') || null;
  const invited = Number((results && results.invited) || 0);
  const runBlockedReason = !hasToken && !envToken ? 'runNoToken' : (!record.get('send_enabled') ? 'runSendBlocked' : null);
  const scanned = Number((funnel && funnel.never_accessed) || 0);
  const templateOptions = (((results && results.templates) || []).map((name) => ({ id: name, name })));

  const clockChip = !lastTick
    ? <Chip color="red" label={tr('clockNever')} />
    : clockAlive
      ? <Chip color="green" label={tr('clockAlive', { minutes: tickMinutes })} />
      : <Chip color="red" label={tr('clockDead', { minutes: tickMinutes })} />;

  const bar = (share, tone) => (
    <div className={classes.resultBar}>
      <span className={clsx(classes.resultBarFill, classes[TONE_CLASS[tone]])} style={{ width: `${Math.min(share, 100)}%` }} />
    </div>
  );

  const summaryValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      const parts = Object.entries(value).map(([reason, count]) => `${tr(`reasons.${reason}`, { defaultValue: reason })}: ${count}`);
      return parts.length ? parts.join(' · ') : '0';
    }
    if (['trigger', 'stopped_reason', 'error'].includes(key)) return tr(`summary.${value}`, { defaultValue: value });
    return `${value}`;
  };

  const statusActions = (
    <div className={classes.paperActions}>
      <Button variant="contained" color="primary" size="small" className={classes.smallButton} onClick={() => setConfirmRun(true)}>
        {tr('runNow')}
      </Button>
      <Button variant="outlined" size="small" className={classes.smallButton} onClick={() => history.push(whatsappCampaignInvitations.spec)}>
        {tr('invitations')}
      </Button>
    </div>
  );

  return (
    <>
      <Paper Icon={WhatsAppIcon} iconColor="green" title={tr('statusTitle')} loading={loading} actions={statusActions}>
        <div className={classes.section}>
          <div className={classes.chips}>
            {record.get('is_enabled')
              ? <Chip color="green" label={tr('autoOn')} />
              : <Chip color="orange" label={tr('autoOff')} />}
            {!record.get('send_enabled') && <Chip color="red" label={tr('sendBlocked')} />}
            {record.get('run_started_at') && <Chip color="orange" label={tr('running')} />}
            {!record.get('run_started_at') && record.get('run_requested_at') && <Chip color="orange" label={tr('runPending')} />}
          </div>
          <GridContainer>
            <GridItem xs={12} md={3}>
              <InputShow label={attrName('last_run_at')} value={lastRun ? moment(lastRun).format('DD/MM/YYYY HH:mm') : tr('neverRan')} />
            </GridItem>
          </GridContainer>

          {summary && (
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  {SUMMARY_KEYS.map((key) => <TableCell key={key}>{tr(`summary.${key}`)}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {SUMMARY_KEYS.map((key) => <TableCell key={key}>{summaryValue(key, summary[key])}</TableCell>)}
                </TableRow>
              </TableBody>
            </Table>
          )}
        </div>
      </Paper>

      <Paper
        Icon={AssessmentIcon}
        iconColor="purple"
        title={tr('resultsTitle')}
        loading={resultsLoading && !results}
        actions={(
          <div className={classes.paperActions}>
            <Button
              variant="contained"
              color="primary"
              className={classes.smallButton}
              startIcon={<SaveAltIcon />}
              disabled={downloading || !invited}
              onClick={() => downloadResultsPdf(resultsParams(template, sentFrom, sentUntil), tr, setDownloading)}
            >
              {tr('downloadPdf')}
            </Button>
          </div>
        )}
      >
        <div className={classes.section}>
          <GridContainer>
            <GridItem xs={12} md={4}>
              <InputControl
                name="template"
                value={template}
                label={tr('resultTemplate')}
                inputComponent={InputSelect}
                onChange={({ template: selected }) => setTemplate(selected || ALL_TEMPLATES)}
                InputProps={{
                  options: [{ id: ALL_TEMPLATES, name: tr('resultTemplateAll') }, ...templateOptions],
                  outValueType: 'string',
                }}
              />
            </GridItem>
            <GridItem xs={12} md={3}>
              <InputControl
                name="sent_from"
                value={sentFrom}
                label={tr('sentFrom')}
                inputComponent={InputDate}
                InputProps={{ type: 'date' }}
                onChange={(date) => setSentFrom(date || '')}
              />
            </GridItem>
            <GridItem xs={12} md={3}>
              <InputControl
                name="sent_until"
                value={sentUntil}
                label={tr('sentUntil')}
                inputComponent={InputDate}
                InputProps={{ type: 'date' }}
                onChange={(date) => setSentUntil(date || '')}
              />
            </GridItem>
          </GridContainer>
          {resultsLoading && results && <CircularProgress size={18} className={classes.inlineProgress} />}
          {results && !invited && <Typography className={classes.emptyState}>{tr(emptyResultsKey(template, sentFrom, sentUntil))}</Typography>}
          {results && Boolean(invited) && (
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.resultStageCell}>{tr('resultStage')}</TableCell>
                  <TableCell align="right">{tr('resultPeople')}</TableCell>
                  <TableCell align="right">{tr('resultShare')}</TableCell>
                  <TableCell className={classes.resultBarCell} />
                </TableRow>
              </TableHead>
              <TableBody>
                {RESULT_ROWS.map(({ key, tone, highlight }) => {
                  const value = Number(results[key] || 0);
                  const share = Math.round((value / invited) * 100);
                  return (
                    <TableRow key={key} className={highlight ? classes.highlightRow : undefined}>
                      <TableCell>{tr(`results.${key}`)}</TableCell>
                      <TableCell align="right">{value.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right" className={classes.resultShareCell}>{key === 'invited' ? '-' : `${share}%`}</TableCell>
                      <TableCell className={classes.resultBarCell}>{bar(share, tone)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </Paper>

      <Paper Icon={PeopleIcon} iconColor="blue" title={tr('audienceTitle')} loading={funnelLoading && !funnel}>
        <div className={classes.section}>
          <Typography className={classes.help}>{tr('previewHelp')}</Typography>
          {funnelLoading && funnel && <CircularProgress size={18} className={classes.inlineProgress} />}
          {funnel && (
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.resultStageCell}>{tr('funnelFilter')}</TableCell>
                  <TableCell align="right">{tr('funnelUsers')}</TableCell>
                  <TableCell className={classes.resultBarCell} />
                </TableRow>
              </TableHead>
              <TableBody>
                {FUNNEL_KEYS.map((key) => (
                  <TableRow key={key} className={key === 'eligible_now' ? classes.highlightRow : undefined}>
                    <TableCell>{tr(`funnel.${key}`)}</TableCell>
                    <TableCell align="right">{Number(funnel[key] || 0).toLocaleString('pt-BR')}</TableCell>
                    <TableCell className={classes.resultBarCell}>
                      {bar(scanned ? (Number(funnel[key] || 0) / scanned) * 100 : 0, key === 'eligible_now' ? 'good' : 'neutral')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Paper>

      <Form onSubmit={onSubmit} isPersisted showCancel={false}>
        <Paper Icon={DescriptionIcon} iconColor="purple" title={tr('templateTitle')} loading={loading}>
          <div className={classes.section}>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  name="template_name"
                  value={record.get('template_name')}
                  label={attrName('template_name')}
                  required
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} md={2}>
                <InputControl
                  name="template_language"
                  value={record.get('template_language')}
                  label={attrName('template_language')}
                  required
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <InputControl
                  name="header_video_url"
                  value={record.get('header_video_url') || ''}
                  label={attrName('header_video_url')}
                  inputComponent={Input}
                  onChange={handleChange}
                />
              </GridItem>
            </GridContainer>
          </div>
        </Paper>

        <Paper
          Icon={ScheduleIcon}
          iconColor="blue"
          title={tr('scheduleTitle')}
          loading={loading}
          actions={<div className={classes.paperActions}>{clockChip}</div>}
        >
          <div className={classes.section}>
            <GridContainer>
              <GridItem xs={12} md={3}>
                <InputControl
                  name="send_hours"
                  value={record.get('send_hours')}
                  label={attrName('send_hours')}
                  required
                  inputComponent={Input}
                  onChange={handleChange}
                  InputProps={{ inputProps: { inputMode: 'numeric', placeholder: '8, 12, 17' } }}
                />
                <Typography className={classes.help}>{tr('sendHoursHelp')}</Typography>
              </GridItem>
              <GridItem xs={12} md={3}>
                <InputControl
                  name="batch_size"
                  value={record.get('batch_size')}
                  label={attrName('batch_size')}
                  required
                  inputComponent={InputNumber}
                  onChange={handleChange}
                  InputProps={numberInputProps}
                />
                <Typography className={classes.help}>{tr('batchHelp')}</Typography>
              </GridItem>
              <GridItem xs={12} md={3}>
                <InputControl
                  name="min_days_since_signup"
                  value={record.get('min_days_since_signup')}
                  label={attrName('min_days_since_signup')}
                  required
                  inputComponent={InputNumber}
                  onChange={handleChange}
                  InputProps={numberInputProps}
                />
                <Typography className={classes.help}>{tr('minDaysHelp')}</Typography>
              </GridItem>
              <GridItem xs={12} md={3}>
                <div className={classes.switchCell}>
                  <InputSwitch
                    value={Boolean(record.get('is_enabled'))}
                    label={attrName('is_enabled')}
                    onChange={(checked) => handleChange({ is_enabled: checked })}
                  />
                </div>
                <Typography className={classes.help}>{tr('enabledHelp')}</Typography>
              </GridItem>
            </GridContainer>
          </div>
        </Paper>
      </Form>

      <Dialog open={confirmRun} onClose={() => setConfirmRun(false)}>
        <DialogTitle>{tr('runNow')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{runBlockedReason ? tr(runBlockedReason) : tr('runNowConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRun(false)}>{t('form-cancel')}</Button>
          <Button color="primary" variant="contained" disabled={Boolean(runBlockedReason)} onClick={runNow}>{tr('runNow')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

Settings.propTypes = {
  history: historyPropType,
};

export default withRouter(Settings);
